import { formatNumWithKMBAlt } from 'utils/helperMethods';
import type { MetricFormat } from '../config/metrics';

const numberFormatters = new Map<number, Intl.NumberFormat>();

function getNumberFormatter(precision: number): Intl.NumberFormat {
    let formatter = numberFormatters.get(precision);
    if (!formatter) {
        formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        });
        numberFormatters.set(precision, formatter);
    }
    return formatter;
}

/** Shared empty-value text. Surfaces with their own sentinel guard before calling. */
export const EMPTY_METRIC_VALUE = '-';

/**
 * The one way TPOGen turns a metric number into display text.
 *
 * `MetricFormat` carries the backend's per-metric display metadata
 * (`precision` -> `allowedDecimals`, `currency_prefix` -> `preUnit`,
 * `currency_suffix` -> `postUnit`; see `metricAdapter.buildMetricFormatIndex`),
 * so a metric reads the same in every grid, report and chart that shows it.
 *
 * The sign is emitted BEFORE `preUnit` (`-$1,234.00`, not `$-1,234.00`) because
 * the adornment belongs to the unit, not to the magnitude.
 */
export function formatMetricValue(value: number | null | undefined, format: MetricFormat): string {
    if (value === undefined || value === null || Number.isNaN(Number(value))) return EMPTY_METRIC_VALUE;
    const num = Number(value);
    const { allowedDecimals, preUnit, postUnit } = format;
    const magnitude = getNumberFormatter(allowedDecimals).format(Math.abs(num));
    return `${num < 0 ? '-' : ''}${preUnit}${magnitude}${postUnit}`;
}

/**
 * Abbreviated (K/M/B) variant for narrow surfaces such as chart axes and
 * tooltips, keeping the metric's precision and adornments so the chart reads the
 * same as the table beside it.
 */
export function formatMetricValueCompact(value: number | null | undefined, format: MetricFormat): string {
    if (value === undefined || value === null || Number.isNaN(Number(value))) return EMPTY_METRIC_VALUE;
    const num = Number(value);
    const { allowedDecimals, preUnit, postUnit } = format;
    const magnitude = formatNumWithKMBAlt(Math.abs(num), allowedDecimals, allowedDecimals);
    return `${num < 0 ? '-' : ''}${preUnit}${magnitude}${postUnit}`;
}
